import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import LoadingSpinner from "../../common/components/LoadingSpinner";

const DisplayRecording = ({ videoBlob, posterImage }) => {
  const videoRef = useRef(null);
  //   const posterRef = useRef(posterImage);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const processedRef = useRef(false); // Add this to prevent reprocessing

  // Cleanup the URL and video src when component unmounts
  useEffect(() => {
    let isMounted = true;
    const videoElement = videoRef.current;

    // Prevent reprocessing the same video
    if (processedRef.current) return;
    processedRef.current = true;

    videoElement.poster = posterImage;

    const createVideo = async () => {
      try {
        if (!videoBlob || videoBlob.size === 0) {
          throw new Error("Invalid video data");
        }

        console.log("Original video blob:", {
          size: videoBlob.size,
          type: videoBlob.type,
          lastModified: videoBlob.lastModified,
        });

        // Create a new blob with explicit type
        const videoBlobWithType = new Blob([videoBlob], {
          type: videoBlob.type || "video/webm",
        });

        // Create object URL from the typed blob
        const videoUrl = URL.createObjectURL(videoBlobWithType);
        console.log("Created object URL:", videoUrl);

        if (!videoElement) return;

        // Set up error handler before setting src
        videoElement.onerror = (e) => {
          console.error("Video element error:", e);
          throw new Error(
            `Video error: ${videoElement.error?.message || "Unknown error"}`
          );
        };

        // Set up loadeddata handler before setting src
        const loadedDataPromise = new Promise((resolve, reject) => {
          videoElement.onloadeddata = () => {
            console.log("Video data loaded successfully");
            resolve();
          };
          videoElement.onerror = () => reject(videoElement.error);
        });

        // Set the source
        videoElement.src = videoUrl;
        console.log("Set video source");

        // Wait for video to be loaded
        await loadedDataPromise;
        console.log("Video loaded successfully");

        // Handle duration Infinity error
        if (videoElement.duration === Infinity) {
          console.log("Handling duration Infinity");
          await new Promise((resolve, reject) => {
            videoElement.currentTime = 1e101;
            videoElement.ontimeupdate = () => {
              videoElement.ontimeupdate = null;
              resolve();
            };
            videoElement.onerror = () => reject(videoElement.error);
          });
        }

        if (isMounted) {
          setIsLoading(false);
        }

        // Cleanup function to revoke the object URL
        return () => {
          URL.revokeObjectURL(videoUrl);
        };
      } catch (err) {
        console.error("Video creation error:", err);
        if (isMounted) {
          setError(`Failed to load video: ${err.message}`);
          setIsLoading(false);
        }
      }
    };

    createVideo();

    return () => {
      isMounted = false;
      if (videoElement) {
        videoElement.poster = "";
        videoElement.src = "";
      }
      processedRef.current = false;
    };
  }, [videoBlob, posterImage]);

  return (
    <div className="w-[640px] h-[480px] flex flex-col justify-end items-center rounded-lg pb-2 mb-2">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
          <LoadingSpinner />
        </div>
      )}
      {error ? (
        <div className="text-red-500 text-center p-4">{error}</div>
      ) : (
        <video
          ref={videoRef}
          className="w-full h-full object-cover rounded-lg mb-4"
          playsInline
          controls
        />
      )}
    </div>
  );
};

DisplayRecording.propTypes = {
  videoBlob: PropTypes.instanceOf(Blob).isRequired,
  posterImage: PropTypes.string,
};

export default DisplayRecording;
